import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Fi from 'react-icons/fi';
import MomentumChart from './MomentumChart.jsx';

const COLOR_A = '#818cf8', COLOR_B = '#fb7185';

const TAG_INFO = {
  winner:        { emoji: '🏸', label: '致勝球',  color: '#34d399' },
  error:         { emoji: '😬', label: '主動失誤', color: '#fb7185' },
  serve_fault:   { emoji: '⚡', label: '發球失誤', color: '#f59e0b' },
  receive_fault: { emoji: '🫳', label: '接發失誤', color: '#a78bfa' },
  out:           { emoji: '↗️', label: '出界',    color: '#60a5fa' },
  net:           { emoji: '🥅', label: '下網',    color: '#f97316' },
};

const computeStats = (pointLog, pts) => {
  if (!pointLog || pointLog.length === 0) return null;
  const threshold = pts >= 21 ? 18 : pts >= 11 ? 9 : Math.floor(pts * 0.85);
  const totalA = pointLog.filter(p => p.scorer === 'A').length;
  const totalB = pointLog.filter(p => p.scorer === 'B').length;
  let maxRunA = 0, maxRunB = 0, curRunA = 0, curRunB = 0;
  pointLog.forEach(p => {
    if (p.scorer === 'A') { curRunA++; curRunB = 0; if (curRunA > maxRunA) maxRunA = curRunA; }
    else { curRunB++; curRunA = 0; if (curRunB > maxRunB) maxRunB = curRunB; }
  });
  const critical = pointLog.filter(p => p.scoreA >= threshold || p.scoreB >= threshold);
  const critA = critical.filter(p => p.scorer === 'A').length;
  const critB = critical.filter(p => p.scorer === 'B').length;
  const servingA = pointLog.filter(p => p.server === 'A');
  const servingB = pointLog.filter(p => p.server === 'B');
  const serveWinA = servingA.filter(p => p.scorer === 'A').length;
  const serveWinB = servingB.filter(p => p.scorer === 'B').length;
  const serveRateA = servingA.length > 0 ? Math.round((serveWinA / servingA.length) * 100) : null;
  const serveRateB = servingB.length > 0 ? Math.round((serveWinB / servingB.length) * 100) : null;
  const tags = pointLog.filter(p => p.tagId);
  const errorTypes = ['error', 'serve_fault', 'receive_fault', 'out', 'net'];
  const tagStats = {};
  errorTypes.forEach(t => {
    tagStats[t] = {
      A: tags.filter(p => p.scorer === 'A' && p.tagId === t).length,
      B: tags.filter(p => p.scorer === 'B' && p.tagId === t).length,
    };
  });
  const winnersA = tags.filter(p => p.scorer === 'A' && p.tagId === 'winner').length;
  const winnersB = tags.filter(p => p.scorer === 'B' && p.tagId === 'winner').length;
  const errorsA = errorTypes.reduce((s, t) => s + tagStats[t].A, 0);
  const errorsB = errorTypes.reduce((s, t) => s + tagStats[t].B, 0);
  return { totalA, totalB, maxRunA, maxRunB, critA, critB, threshold, serveRateA, serveRateB, serveCountA: servingA.length, serveCountB: servingB.length, tagStats, winnersA, winnersB, errorsA, errorsB, hasTags: tags.length > 0 };
};

/* ─── Stat Row ─── */
const StatRow = ({ label, vA, vB, colorA, colorB, unit = '', highlight, big }) => {
  const numA = typeof vA === 'number' ? vA : null;
  const numB = typeof vB === 'number' ? vB : null;
  const total = numA !== null && numB !== null ? numA + numB : 0;
  const pctA = total > 0 ? (numA / total) * 100 : 50;

  return (
    <div className={`flex items-center gap-3 py-3 px-4 rounded-2xl ${highlight ? 'bg-white/[0.07] border border-white/12' : 'bg-white/[0.03]'}`}>
      <span className={`font-bold text-white/50 flex-1 min-w-0 ${big ? 'text-sm' : 'text-xs'}`}>{label}</span>
      <span className={`font-black tabular-nums shrink-0 ${big ? 'text-2xl' : 'text-lg'}`} style={{ color: colorA }}>
        {vA ?? '-'}{unit && numA !== null ? unit : ''}
      </span>
      {total > 0 && (
        <div className="w-16 h-2 rounded-full bg-white/10 overflow-hidden shrink-0">
          <div className="h-full rounded-l-full" style={{ width: `${pctA}%`, backgroundColor: colorA, opacity: 0.8 }} />
        </div>
      )}
      <span className={`font-black tabular-nums shrink-0 ${big ? 'text-2xl' : 'text-lg'}`} style={{ color: colorB }}>
        {vB ?? '-'}{unit && numB !== null ? unit : ''}
      </span>
    </div>
  );
};

/* ─── Tag Bar Row ─── */
const TagBarRow = ({ tagKey, vA, vB }) => {
  const info = TAG_INFO[tagKey];
  if (!info) return null;
  const total = (vA || 0) + (vB || 0);
  if (total === 0) return null;
  const pctA = total > 0 ? (vA / total) * 100 : 50;

  return (
    <div className="flex items-center gap-3 py-2.5 px-4 rounded-xl bg-white/[0.03]">
      <span className="text-base w-6 text-center shrink-0">{info.emoji}</span>
      <span className="text-xs font-black shrink-0 w-16" style={{ color: info.color }}>{info.label}</span>
      <span className="text-base font-black tabular-nums w-7 text-right shrink-0" style={{ color: COLOR_A }}>{vA}</span>
      <div className="flex-1 h-2.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-l-full transition-all duration-700"
          style={{ width: `${pctA}%`, backgroundColor: COLOR_A, opacity: 0.8 }} />
      </div>
      <span className="text-base font-black tabular-nums w-7 shrink-0" style={{ color: COLOR_B }}>{vB}</span>
    </div>
  );
};

/* ─── Section ─── */
const Section = ({ title, children }) => (
  <div className="mb-5">
    <div className="flex items-center gap-2 mb-2.5">
      <div className="h-px flex-1 bg-white/8" />
      <p className="text-[10px] font-black text-white/35 uppercase tracking-widest whitespace-nowrap">{title}</p>
      <div className="h-px flex-1 bg-white/8" />
    </div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

/* ─── Team Header ─── */
const TeamHeader = ({ nameA, nameB, colorA, colorB }) => (
  <div className="flex items-center justify-between px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/10 mb-4">
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colorA }} />
      <span className="text-sm font-black" style={{ color: colorA }}>{nameA}</span>
    </div>
    <span className="text-xs text-white/20 font-black">VS</span>
    <div className="flex items-center gap-2">
      <span className="text-sm font-black" style={{ color: colorB }}>{nameB}</span>
      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colorB }} />
    </div>
  </div>
);

/* ─── Main ─── */
const StatsScreen = ({ open, onClose, allPointLogs, teamNameA, teamNameB, pts, gamesA, gamesB, bestOf }) => {
  const gameCount = allPointLogs?.length ?? 0;
  const [activeGame, setActiveGame] = useState(0);

  const currentLog = useMemo(() => {
    if (activeGame === 0) return allPointLogs?.flat() ?? [];
    return allPointLogs?.[activeGame - 1] ?? [];
  }, [activeGame, allPointLogs]);

  const stats = useMemo(() => computeStats(currentLog, pts), [currentLog, pts]);
  const pct = (n, d) => d > 0 ? `${Math.round((n / d) * 100)}%` : '-';

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[300] flex flex-col">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/90 backdrop-blur-md" onClick={onClose} />

          <motion.div
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
            className="relative z-10 flex flex-col h-full bg-[#060f1e]"
          >
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between px-5 border-b border-white/[0.08]"
              style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingBottom: '1rem' }}>
              <div>
                <h2 className="text-xl font-black text-white">比賽統計</h2>
                <p className="text-xs text-white/40 font-bold mt-0.5">
                  <span style={{ color: COLOR_A }}>{teamNameA}</span>
                  <span className="text-white/20 mx-1.5">vs</span>
                  <span style={{ color: COLOR_B }}>{teamNameB}</span>
                  <span className="ml-2 text-white/30">{gamesA} : {gamesB} 局</span>
                </p>
              </div>
              <button onClick={onClose}
                className="bg-white/[0.08] hover:bg-white/[0.15] text-white/70 hover:text-white p-2.5 rounded-xl transition-all">
                <Fi.FiX className="text-xl" />
              </button>
            </div>

            {/* Game Tabs */}
            {gameCount > 1 && (
              <div className="shrink-0 flex gap-1.5 px-5 py-2.5 border-b border-white/[0.05] overflow-x-auto"
                style={{ scrollbarWidth: 'none' }}>
                {[{ label: '全場', id: 0 }, ...Array.from({ length: gameCount }, (_, i) => ({ label: `第${i + 1}局`, id: i + 1 }))].map(tab => (
                  <button key={tab.id} onClick={() => setActiveGame(tab.id)}
                    className="shrink-0 px-4 py-2 rounded-xl font-black text-sm transition-all"
                    style={{
                      backgroundColor: activeGame === tab.id ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.05)',
                      color: activeGame === tab.id ? '#a5b4fc' : 'rgba(255,255,255,0.4)',
                      border: `1px solid ${activeGame === tab.id ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.08)'}`,
                    }}>
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4" style={{ scrollbarWidth: 'none' }}>
              {!stats ? (
                <div className="flex items-center justify-center h-40 text-white/30 text-sm font-bold">暫無統計數據</div>
              ) : (
                <>
                  <TeamHeader nameA={teamNameA} nameB={teamNameB} colorA={COLOR_A} colorB={COLOR_B} />

                  <Section title="總覽">
                    <StatRow label="總得分" vA={stats.totalA} vB={stats.totalB} colorA={COLOR_A} colorB={COLOR_B} highlight big />
                    <StatRow label="最大連得" vA={stats.maxRunA} vB={stats.maxRunB} colorA={COLOR_A} colorB={COLOR_B} unit=" 分" />
                  </Section>

                  <Section title="比賽動量">
                    <MomentumChart pointLog={currentLog} nameA={teamNameA} nameB={teamNameB} colorA={COLOR_A} colorB={COLOR_B} />
                  </Section>

                  <Section title={`關鍵分（${stats.threshold} 分後）`}>
                    <StatRow label="關鍵分得分" vA={stats.critA} vB={stats.critB} colorA={COLOR_A} colorB={COLOR_B} highlight />
                    <StatRow label="關鍵得分率" vA={pct(stats.critA, stats.critA + stats.critB)} vB={pct(stats.critB, stats.critA + stats.critB)} colorA={COLOR_A} colorB={COLOR_B} />
                  </Section>

                  <Section title="發球表現">
                    <StatRow label="發球得分率" vA={stats.serveRateA != null ? `${stats.serveRateA}%` : '-'} vB={stats.serveRateB != null ? `${stats.serveRateB}%` : '-'} colorA={COLOR_A} colorB={COLOR_B} highlight />
                    <StatRow label="發球局數" vA={stats.serveCountA} vB={stats.serveCountB} colorA={COLOR_A} colorB={COLOR_B} />
                  </Section>

                  {stats.hasTags && (
                    <Section title="得分 / 失誤分析">
                      <StatRow label="🏸 致勝球" vA={stats.winnersA} vB={stats.winnersB} colorA={COLOR_A} colorB={COLOR_B} highlight />
                      <StatRow label="總失誤" vA={stats.errorsA} vB={stats.errorsB} colorA={COLOR_A} colorB={COLOR_B} />

                      {/* Error breakdown */}
                      <div className="mt-2 p-3 rounded-2xl bg-white/[0.03] border border-white/[0.07] space-y-1">
                        <p className="text-[10px] font-black text-white/30 uppercase tracking-widest mb-2">失誤細項</p>
                        {['error', 'serve_fault', 'receive_fault', 'out', 'net'].map(key => (
                          <TagBarRow key={key} tagKey={key}
                            vA={stats.tagStats[key]?.A ?? 0}
                            vB={stats.tagStats[key]?.B ?? 0} />
                        ))}
                      </div>
                    </Section>
                  )}

                  {!stats.hasTags && (
                    <div className="p-4 rounded-2xl border border-white/[0.07] bg-white/[0.02] text-center mt-2">
                      <p className="text-white/30 text-sm font-bold">啟用失誤標記後，可查看失誤細項統計</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default StatsScreen;